import React, { useState, useRef, useEffect } from "react";
import { Divider, Spin } from "antd";
import YupupScreen from "../../assets/icons/blackLogo.svg";
import { LoadingOutlined } from "@ant-design/icons";
import io from "socket.io-client";

// Api
import { getMessages, createMessage, uploadFile } from "../../api/messages";

// Components
import Message from "./Message";
import Button from "../../components/common/button";
import ChatInput from "../../components/common/ChatInput";
import FileMessage from "./FileMessage";

// Constants
import { DEFAULT_MESSAGES_PAGE_SIZE } from "../../share/constants/messages";

function Messages({ selectedRoom }) {
  const [messagesData, setMessagesData] = useState({
    items: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateMessageLoading, setIsCreateMessageLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isClosedState, setIsClosedState] = useState(false);
  const [closedMessage, setClosedMessage] = useState("");

  // Ref
  const messageDataRef = useRef(null);
  const msgContainerRef = useRef(null);
  const newMsgRef = useRef(null);
  const selectedRoomRef = useRef(null);

  useEffect(() => {
    if (selectedRoom?._id) {
      const businessName = selectedRoom?.business?.businessName;
      const isBusiness = selectedRoom?.isBusiness;
      const closedBy = isBusiness
        ? businessName
        : `${selectedRoom?.user?.firstName} ${selectedRoom?.user?.lastName}`;
      const closedMessage = `Chat has been closed by ${closedBy}`;
      setClosedMessage(closedMessage);
      setIsClosedState(selectedRoom?.isClosed);
      selectedRoomRef.current = selectedRoom;
      setIsFetchingMore(false);
      setIsLoading(false);
      handleGetMessages();
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (selectedRoom?._id) {
      const socket = io(process.env.REACT_APP_API_KEY);

      // Assuming chatId is available
      const chatRoom = `chat_${selectedRoom?._id}`;

      // Join the room
      socket.emit("joinRoom", chatRoom);

      // Event listener for 'message' event
      socket.on("message", (data) => {
        const updatedMessages = [...messageDataRef?.current?.items, data];

        const updatedState = {
          ...messageDataRef?.current,
          items: updatedMessages,
        };
        setMessagesData(updatedState);
        messageDataRef.current = updatedState;
      });

      // Event listener for 'RoomClose' event
      socket.on("roomClose", (data) => {
        setClosedMessage(data?.closedMessage);
        setIsClosedState(data?.isClosed);
      });

      // Clean up the socket connection on component unmount
      return () => {
        socket.emit("leaveRoom", chatRoom);
        socket.disconnect();
      };
    }
  }, [selectedRoom?._id]);

  useEffect(() => {
    msgContainerRef.current?.addEventListener("scroll", () => {
      const container = msgContainerRef.current;
      if (container) {
        if (
          container?.scrollTop === 0 &&
          messageDataRef?.current?.totalPages >
            messageDataRef?.current?.currentPage &&
          !isFetchingMore
        ) {
          setIsFetchingMore(true);
          handleGetMoreMessages();
        }

        const threshold = 1;

        if (
          container &&
          Math.abs(
            container.scrollHeight -
              container.scrollTop -
              container.clientHeight
          ) <= threshold
        ) {
          setIsFetchingMore(false);
        }
      }
    });

    return () => {
      msgContainerRef.current?.removeEventListener("scroll", () => {});
    };
  }, [msgContainerRef.current]);

  useEffect(() => {
    if (!isFetchingMore) {
      newMsgRef.current?.scrollIntoView();
    }
  }, [messagesData?.items, isFetchingMore]);

  const handleGetMessages = async () => {
    setIsLoading(true);
    const res = await getMessages(
      1,
      DEFAULT_MESSAGES_PAGE_SIZE,
      selectedRoomRef?.current?._id
    );
    if (res) {
      setIsLoading(false);
      const messagesResponse = {
        ...res,
        items: res?.items?.reverse(),
      };
      messageDataRef.current = messagesResponse;
      newMsgRef.current?.scrollIntoView();
      setMessagesData(messagesResponse);
    }
  };

  const handleGetMoreMessages = async () => {
    const res = await getMessages(
      messageDataRef?.current?.currentPage + 1,
      DEFAULT_MESSAGES_PAGE_SIZE,
      selectedRoomRef?.current?._id
    );

    const recentViewedMsgId = messageDataRef?.current?.items?.at(1);
    const getRecentViewedMsg = document.getElementById(
      `message-${recentViewedMsgId?._id}`
    );

    getRecentViewedMsg?.scrollIntoView();

    if (res) {
      const updatedMessages = [
        ...res?.items?.reverse(),
        ...messageDataRef?.current?.items,
      ];
      const updatedState = {
        ...res,
        items: updatedMessages,
      };
      messageDataRef.current = updatedState;
      setMessagesData(updatedState);
    }
  };

  const handleCreateMessage = async (payload) => {
    setIsCreateMessageLoading(true);
    const response = await createMessage(payload);
    if (response) {
      setMessage(null);
      setUploadedFile(null);
      setIsCreateMessageLoading(false);
    }
  };

  const handleCreateTextMessage = async () => {
    if (message || uploadedFile) {
      const messagePayload = {
        isFile: uploadedFile ? true : false,
      };

      if (uploadedFile) {
        messagePayload.file = uploadedFile;
      }

      if (message) {
        messagePayload.text = message;
      }

      const payload = {
        chatRoomId: selectedRoom?._id,
        businessId: selectedRoom?.business?._id,
        customerUserId: selectedRoom?.user?._id,
        isBusiness: true,
        message: messagePayload,
      };

      await handleCreateMessage(payload);
    }
  };

  const handleUpload = async (info) => {
    setIsCreateMessageLoading(true);
    const file = info?.file;

    if (file) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await uploadFile(formData);
        if (response?.url) {
          setUploadedFile(response?.url);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsCreateMessageLoading(false);
      }
    } else {
      setIsCreateMessageLoading(false);
    }
  };

  const handleDeleteUploadFileIcon = () => {
    setUploadedFile(null);
  };

  const handleMessageOnChange = (event) => {
    setMessage(event.target.value);
  };

  const handleMessageOnEnter = (event) => {
    if (event.key === "Enter") {
      // Do whatever you want to do when Enter is pressed
      if ((message || uploadedFile) && !isCreateMessageLoading) {
        handleCreateTextMessage();
      }
    }
  };

  // Constant
  const userName = `${selectedRoom?.user?.firstName} ${selectedRoom?.user?.lastName}`;
  return (
    <div className="flex flex-col justify-between messages-main-container border border-1 border-gray-200  rounded">
      {selectedRoom ? (
        <>
          <div>
            <div className="flex justify-between items-center px-2 py-1 m-0">
              <div>
                <p className="text-base m-0 font-bold">{userName}</p>
              </div>
              <div>
                <Button
                  type="primary"
                  className="bg-primaryOrange border border-2 border-primaryOrange text-sm hover:!bg-white hover:!border-primaryOrange hover:!text-black room-delete-btn-height "
                  title={"Redeemed"}
                />
              </div>
            </div>
            <Divider className=" h-1px m-0" plain />
          </div>

          <div
            className="p-2 messages-container flex-grow"
            ref={msgContainerRef}
          >
            {isLoading ? (
              <div className="w-100% h-full flex items-center justify-center py-3">
                <Spin indicator={<LoadingOutlined />} size="medium" />
              </div>
            ) : (
              <div>
                {!isLoading && messagesData?.items?.length > 0 ? (
                  messagesData?.items?.map((item) => {
                    return (
                      <div key={item?._id} id={`message-${item?._id}`}>
                        {" "}
                        {item?.message?.isFile ? (
                          <FileMessage
                            message={item}
                            selectedRoom={selectedRoom}
                          />
                        ) : (
                          <Message message={item} selectedRoom={selectedRoom} />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="w-100% h-full flex items-center justify-center">
                    No Messages Yet
                  </div>
                )}
              </div>
            )}

            <div sx={{ height: "2px" }} ref={newMsgRef} />
          </div>
          {isClosedState ? (
            <div className="px-2 pb-2">
              <p className="text-center text-grey">{closedMessage}</p>
            </div>
          ) : (
            <div className="px-2 pb-2 cursor-pointer">
              <ChatInput
                key={1}
                value={message}
                onChange={handleMessageOnChange}
                isCreateMessageLoading={isCreateMessageLoading}
                handleCreateTextMessage={handleCreateTextMessage}
                uploadedFile={uploadedFile}
                handleUpload={handleUpload}
                onKeyDown={handleMessageOnEnter}
                handleDeleteUploadFileIcon={handleDeleteUploadFileIcon}
                className="w-100% bg-inputFieldGrey hover:border-primaryBlue h-5.4"
                placeholder="Enter Text Here..."
              />
            </div>
          )}
        </>
      ) : (
        <div className="flex h-full justify-center items-center">
          <img
            className="block h-12 xl:h-15.3 xl:w-26.9"
            alt="yupup-screen-logo"
            src={YupupScreen}
          />
        </div>
      )}
    </div>
  );
}

export default Messages;
